import Fancybox from './modules/fancybox.js';
import Swiper from './modules/swiper.js';
import { a11y, navigation, thumbs, pagination } from './modules/swiper.js';

class ProductMediaSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.options = JSON.parse(this.dataset.options);
    this.options.modules = [a11y];

    if (this.options.pagination) {
      this.options.modules.push(pagination);
    }

    if (this.options.navigation) {
      this.options.modules.push(navigation);
    }

    // Create elements
    this.thumbnailSwiper();
    this.mainSwiper();
    this.fancybox();

    // Reduce actions (So the event can also be removed)
    this.slideToVariantHandler = event => this.slideToVariant(event);

    // Listen for variant change and go to slide
    document.addEventListener('variant-change', this.slideToVariantHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('variant-change', this.slideToVariantHandler);
  }

  thumbnailSwiper() {
    if (!Swiper) return false;

    this.swiperThumbs = this.querySelector('[data-product-media-thumbnails-slider]');
    this.swiperThumbsOptions = {
      a11y: true,
      observer: true,
      spaceBetween: 12,
      slidesPerView: 'auto',
      centerInsufficientSlides: true,
      modules: [a11y, thumbs],
    };

    this.swiperThumbsInstance = new Swiper(this.swiperThumbs, this.swiperThumbsOptions);

    if (Shopify.designMode && this.swiperThumbsInstance) {
      window.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          this.swiperThumbsInstance.update();
        }, 300);
      });
    }
  }

  mainSwiper() {
    if (!Swiper) return false;

    this.swiperMain = this.querySelector('[data-product-media-swiper]');

    let thumbOptions;
    if (this.swiperThumbsInstance) {
      this.options.modules.push(thumbs);
      thumbOptions = {
        thumbs: {
          swiper: this.swiperThumbsInstance.hostEl,
          slideThumbActiveClass: 'swiper-slide-thumb-active',
          multipleActiveThumbs: false,
          autoScrollOffset: 1,
        },
      };
    }
    let paginationOptions = {};
    if (this.options.pagination) {
      paginationOptions = {
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`;
        },
      };
    }

    let initialSlideIndex = 0;
    const initialVariantId = parseInt(this.swiperMain.dataset.productMediaSlider);

    if (initialVariantId) {
      const initialSlide = this.matchVariantIdWithSlide(initialVariantId);
      initialSlideIndex = initialSlide ? initialSlide : initialSlideIndex;
    }

    this.swiperMainOptions = {
      a11y: true,
      rewind: true,
      observer: true,
      initialSlide: initialSlideIndex,
      modules: this.options.modules,
      ...this.options,
      ...thumbOptions,
      ...paginationOptions,
    };

    this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

    if (Shopify.designMode && this.swiperMainInstance) {
      window.addEventListener('shopify:section:load', () => {
        this.swiperMainInstance.update();
      });
    }
  }

  fancybox() {
    if (!Fancybox) return false;

    Fancybox.bind('[data-fancybox]', {
      dragToClose: false,
      showClass: 'f-fadeIn',
      hideClass: 'f-fadeOut',
      mainClass: 'fancybox-products',
      Carousel: {
        transition: 'slide',
      },
      // Disable image zoom animation on opening and closing
      Images: {
        zoom: false,
      },
      Thumbs: {
        type: 'classic',
      },
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
    });
  }

  slideToVariant(event) {
    if (event?.detail?.variant) {
      const slideIndex = this.matchVariantIdWithSlide(+event.detail.variant);
      if (typeof slideIndex == 'number') this.goToSlide(slideIndex);
    }
  }

  matchVariantIdWithSlide(id) {
    if (!id || !this.swiperMain) return false;

    const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

    const slide = [...slides].find((slide) => {
      const slideVariants = JSON.parse(slide.dataset.productVariantImage);
      if (slideVariants.includes(id)) return slide;
    });

    if (!slide) return false;

    return this.getNodeIndex(slide);
  }

  goToSlide(slide) {
    if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) return false;

    const index = typeof slide == 'number' ? parseInt(slide) : this.getNodeIndex(slide);

    this.swiperMainInstance.slideTo(index);
  }

  getNodeIndex(node) {
    return Array.from(node.parentNode.children).indexOf(node);
  }
}

if (!customElements.get('product-media-swiper')) {
  customElements.define('product-media-swiper', ProductMediaSlider);
}